import { useState, Dispatch, SetStateAction } from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';

import CandidateInterviewDateTime, { InterviewScheduleSteps } from './candidate-interview-datetime';
import { CandidateModalStatusChangeScreen } from './discard-candidate/candidate-state-change-confirmation';
import { selectedJobApplicationAtom } from '../../../employer-job-listing.atoms';

import { cn } from '@restworld/utils-common';
function CandidateInterview({ firstName, jobPositionId, workerId, isEdit, interviewDate }: CandidateInterviewProps) {
  // Editing an already scheduled interview skips the "you are moving X" confirmation
  // and opens straight on the date/time picker prefilled with the current values.
  const [interviewScheduleStep, setInterviewScheduleStep] = useState(
    isEdit ? InterviewScheduleSteps.SELECT_DATE_TIME : InterviewScheduleSteps.ACTION_CONFIRMATION
  );
  // Whether the employer entered a date (reminders on) or moved the candidate without one.
  const [withReminders, setWithReminders] = useState(false);

  return (
    <MainWrapper>
      <InnerWrapper>
        {interviewScheduleStep === InterviewScheduleSteps.ACTION_CONFIRMATION ? (
          <CandidateInterviewActionConfirmation setStep={setInterviewScheduleStep} />
        ) : interviewScheduleStep === InterviewScheduleSteps.VIEW_SUCCESS_MESSAGE ? (
          <CandidateInterviewActionConfirmation isCompleted isEdit={isEdit} withReminders={withReminders} />
        ) : (
          <CandidateInterviewDateTime
            firstName={firstName}
            setStep={setInterviewScheduleStep}
            step={interviewScheduleStep}
            jobPositionId={jobPositionId}
            workerId={workerId}
            isEdit={isEdit}
            interviewDate={interviewDate}
            onRemindersChange={setWithReminders}
          />
        )}
      </InnerWrapper>
    </MainWrapper>
  );
}
export default CandidateInterview;

const MainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full h-full', className)} {...props} />
);
const InnerWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('mx-auto flex flex-col justify-between  text-center h-full', className)} {...props} />
);

interface CandidateInterviewProps {
  firstName: string;
  jobPositionId: string;
  workerId: string;
  onClose: () => void;
  isEdit?: boolean;
  interviewDate?: string | null;
}

function CandidateInterviewActionConfirmation({
  setStep,
  isCompleted,
  isEdit,
  withReminders,
}: {
  isCompleted?: boolean;
  isEdit?: boolean;
  withReminders?: boolean;
  setStep?: Dispatch<SetStateAction<InterviewScheduleSteps>>;
}) {
  const { t } = useTranslation('job-positions-page');
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);
  const candidateName = selectedJobApplication?.worker?.name ?? '';

  return (
    <CandidateModalStatusChangeScreen
      topIcon={<UserGroupIcon className="h-6 w-6 text-neutral-700" />}
      defaultText={`${t('you_are_moving_name_in_candidates_to_be_interviewed', { name: candidateName })}.`}
      successText={
        isEdit
          ? t('interview_datetime_updated')
          : t('you_moved_name_in_candidates_to_be_interviewed', { name: candidateName })
      }
      handleSubmit={() => {
        setStep && setStep(InterviewScheduleSteps.SELECT_DATE_TIME);
      }}
      // Only on a first scheduling with a date: on edit the candidate gets the "date changed" email, not a recap.
      subtext={isCompleted && withReminders && !isEdit ? t('reminders_activated_subtext', { name: candidateName }) : ''}
      isCompletedSuccessfully={isCompleted}
    />
  );
}
